import { Label, LabelProps } from '@globalmed/atlas-ui';
import Paragraph from '@tiptap/extension-paragraph';
import { Placeholder } from '@tiptap/extensions';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TextEditorMenu } from './TextEditorMenu';
import './TextEditor.css';

const SmallParagraph = Paragraph.extend({
  addAttributes() {
    return {
      class: {
        default: null,
        renderHTML: (attributes: { class: string }) => {
          return {
            class: `${attributes.class}`,
          };
        },
      },
    };
  },
});

type TProps = {
  labelProps?: Omit<LabelProps, 'disabled'>;
  onChange?: (val: string) => void;
  placeholder?: string;
  value?: string;
  error?: boolean;
  disabled?: boolean;
  'aria-label'?: string;
};

/**
 * Rich text editor component using TipTap with custom paragraph styles, placeholder, and formatting menu.
 * @param props - Component props.
 * @param props.onChange - Callback invoked with the editor content (JSON string) on change.
 * @param props.placeholder - Optional placeholder text for the editor.
 * @param props.value - Optional controlled value for the editor content (JSON string).
 * @param props.error - Whether to display the editor in an error state.
 * @param props.disabled - Whether the editor is read-only.
 * @param props.labelProps - Optional visible label for the editor (also used as its accessible name).
 * @param props.aria-label - Optional accessible name for the editor when no visible label is rendered.
 * @returns JSX.Element - A TipTap editor with a formatting menu.
 */
export const TextEditor = ({
  onChange,
  placeholder,
  value,
  error = false,
  disabled = false,
  labelProps,
  'aria-label': ariaLabel,
}: TProps) => {
  const { t } = useTranslation();
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        paragraph: false,
      }),
      SmallParagraph,
      Placeholder.configure({
        placeholder: placeholder ?? t('editor.placeholder'),
      }),
    ],
    content: value ? JSON.parse(value) : '',
    onUpdate({ editor }) {
      onChange?.(JSON.stringify(editor.getJSON()));
    },
    editable: !disabled,
    editorProps: {
      attributes: {
        'aria-label': labelProps?.label ?? ariaLabel ?? t('editor.placeholder'),
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-invalid': error ? 'true' : 'false',
      },
    },
  });

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [disabled, editor]);

  useEffect(() => {
    if (value && editor) {
      const current = JSON.stringify(editor.getJSON());
      if (current !== value) {
        editor.commands.setContent(JSON.parse(value));
      }
    }
  }, [value, editor]);

  return (
    <div>
      {labelProps && <Label disabled={disabled} {...labelProps} />}
      {!disabled && <TextEditorMenu editor={editor} />}
      <EditorContent editor={editor} className={error ? 'errorEditor' : ''} />
    </div>
  );
};
